import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Bell,
  Code2,
  FileCode,
  LogIn,
  LogOut,
  Megaphone,
  Menu,
  MessageSquare,
  Radio,
  Settings,
  Shield,
  Sparkles,
  Users,
  X,
} from "lucide-react";
import { adminEmails } from "../../../constants.ts";
import { db } from "../db.ts";

export type Tab =
  | "events"
  | "taste"
  | "subscriptions"
  | "friends"
  | "settings"
  | "stats"
  | "promote"
  | "admin";

const tabDef = (id: Tab, label: string, icon: typeof Radio) => ({
  id,
  label,
  icon,
});

export const baseTabs = [
  tabDef("events", "Events", Radio),
  tabDef("taste", "Train Your Taste", Sparkles),
  tabDef("subscriptions", "Subscriptions", Bell),
  tabDef("friends", "Friends", Users),
  tabDef("settings", "Settings", Settings),
  tabDef("stats", "Live Stats", BarChart3),
  tabDef("promote", "Promote", Megaphone),
];

export const adminTab = tabDef("admin", "Admin", Shield);

const tabToPath = (tabId: Tab): string => {
  if (tabId === "taste") return "/swipe";
  if (tabId === "subscriptions") return "/subscriptions";
  if (tabId === "friends") return "/friends";
  if (tabId === "settings") return "/settings";
  if (tabId === "stats") return "/stats";
  if (tabId === "promote") return "/promote";
  if (tabId === "admin") return "/admin";
  return "/events";
};

export const AppHeader = ({
  activeTab,
  onTabChange,
}: {
  activeTab?: Tab | null;
  onTabChange?: (tab: Tab) => void;
}) => {
  const { user } = db.useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isAdmin = Boolean(user?.email && adminEmails.includes(user.email));

  const { data: userData } = db.useQuery(
    user?.id
      ? {
        $users: {
          $: { where: { id: user.id } },
        },
      }
      : null,
  );

  const currentUser = userData?.$users?.[0];
  const tabs = isAdmin ? [...baseTabs, adminTab] : baseTabs;

  const handleTabClick = (tabId: Tab) => {
    if (onTabChange) {
      onTabChange(tabId);
    } else {
      navigate(tabToPath(tabId));
    }
    setMobileMenuOpen(false);
  };

  const navContent = (
    <div className="flex flex-col h-full justify-between gap-6">
      {/* Top section: Logo + Tabs + Dev links */}
      <div className="space-y-6">
        {/* Brand */}
        <Link
          to="/events"
          onClick={() => setMobileMenuOpen(false)}
          className="flex items-center gap-3 px-2 py-1.5 group transition-transform active:scale-95"
        >
          <img
            src="/logo.jpg"
            alt="Agent FOMO"
            className="h-9 w-9 rounded-xl object-cover shadow-md shadow-purple-500/20 border border-purple-500/20 group-hover:scale-105 transition-transform"
          />
          <div className="flex flex-col">
            <span className="text-base font-black tracking-tight text-white group-hover:text-purple-200 transition-colors leading-tight">
              AGENT{" "}
              <span className="bg-gradient-to-r from-purple-400 via-pink-500 to-rose-400 bg-clip-text text-transparent">
                FOMO
              </span>
            </span>
            <span className="text-[10px] font-bold tracking-wider uppercase text-purple-400/80">
              Underground Guide
            </span>
          </div>
        </Link>

        {/* Primary Navigation */}
        <div className="space-y-1">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-3 mb-2">
            Explore
          </p>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabClick(tab.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all group ${
                  active
                    ? "bg-purple-600 text-white shadow-lg shadow-purple-600/25 font-semibold"
                    : "text-gray-400 hover:text-white hover:bg-white/[0.06]"
                }`}
              >
                <Icon
                  size={18}
                  className={`shrink-0 transition-transform group-hover:scale-110 ${
                    active
                      ? "text-white"
                      : "text-gray-400 group-hover:text-white"
                  }`}
                />
                <span className="truncate">{tab.label}</span>
                {tab.id === "taste" && (
                  <span className="ms-auto text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-purple-500/20 text-purple-300 border border-purple-500/30">
                    AI
                  </span>
                )}
                {tab.id === "admin" && (
                  <span className="ms-auto text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-amber-500/20 text-amber-300 border border-amber-500/30">
                    Admin
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Developer & Community Links */}
        <div className="space-y-1 pt-2 border-t border-white/5">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-3 mb-2">
            Resources
          </p>
          <Link
            to="/docs"
            onClick={() => setMobileMenuOpen(false)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-medium text-gray-400 hover:text-purple-300 hover:bg-white/[0.04] transition group"
          >
            <Code2
              size={16}
              className="text-purple-400 shrink-0 group-hover:scale-110 transition-transform"
            />
            <span className="truncate">Developer API</span>
          </Link>
          <a
            href="/llms.txt"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileMenuOpen(false)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-medium text-gray-400 hover:text-white hover:bg-white/[0.04] transition group"
          >
            <FileCode
              size={16}
              className="text-gray-400 shrink-0 group-hover:scale-110 transition-transform"
            />
            <span className="truncate">llms.txt</span>
          </a>
          <a
            href="https://wa.me/972504244206"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileMenuOpen(false)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-medium text-gray-400 hover:text-emerald-300 hover:bg-white/[0.04] transition group"
          >
            <MessageSquare
              size={16}
              className="text-emerald-400 shrink-0 group-hover:scale-110 transition-transform"
            />
            <span className="truncate">WhatsApp Agent</span>
          </a>
        </div>
      </div>

      {/* Bottom section: User Account */}
      <div className="pt-3 border-t border-white/10">
        {user
          ? (
            <div className="p-2.5 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-purple-600 to-pink-600 flex items-center justify-center shrink-0 shadow-sm text-xs font-bold text-white uppercase">
                  {(user.email || "U")[0]}
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    className="text-xs font-semibold text-white truncate font-mono"
                    title={user.email}
                  >
                    {user.email}
                  </p>
                  {currentUser?.phone
                    ? (
                      <p className="text-[11px] text-gray-400 truncate font-mono">
                        {currentUser.phone}
                      </p>
                    )
                    : (
                      <p className="text-[10px] text-emerald-400 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block shrink-0" />
                        Connected
                      </p>
                    )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => db.auth.signOut()}
                className="p-1.5 text-gray-400 hover:text-rose-400 rounded-lg hover:bg-white/10 transition shrink-0"
                title="Sign Out"
                aria-label="Sign Out"
              >
                <LogOut size={16} />
              </button>
            </div>
          )
          : (
            <button
              type="button"
              onClick={() => {
                navigate("/events");
                setMobileMenuOpen(false);
              }}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-semibold transition shadow-md shadow-purple-600/20 active:scale-[0.98]"
            >
              <LogIn size={15} />
              Sign In
            </button>
          )}
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Left Sidebar Navbar */}
      <aside className="hidden md:flex flex-col w-64 lg:w-72 h-screen sticky top-0 bg-[#121214]/95 backdrop-blur-xl border-r border-white/10 p-4 z-40 shrink-0 overflow-y-auto">
        {navContent}
      </aside>

      {/* Mobile Top Header */}
      <header className="md:hidden sticky top-0 z-40 bg-[#121214]/95 backdrop-blur-xl border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <Link
          to="/events"
          className="flex items-center gap-2.5 group transition-transform active:scale-95"
        >
          <img
            src="/logo.jpg"
            alt="Agent FOMO"
            className="h-8 w-8 rounded-lg object-cover shadow-md shadow-purple-500/20 border border-purple-500/20"
          />
          <span className="text-base font-black tracking-tight text-white leading-tight">
            AGENT{" "}
            <span className="bg-gradient-to-r from-purple-400 via-pink-500 to-rose-400 bg-clip-text text-transparent">
              FOMO
            </span>
          </span>
        </Link>

        <div className="flex items-center gap-2">
          {user
            ? (
              <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-purple-600 to-pink-600 flex items-center justify-center text-xs font-bold text-white uppercase shadow-sm">
                {(user.email || "U")[0]}
              </div>
            )
            : (
              <button
                type="button"
                onClick={() => navigate("/events")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-xs font-semibold text-white transition shadow-sm"
              >
                <LogIn size={14} />
                Sign In
              </button>
            )}

          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/5 transition"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      {/* Mobile Drawer Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm animate-fade-in"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Drawer content */}
          <div className="relative w-4/5 max-w-xs h-full bg-[#121214] border-r border-white/10 p-5 flex flex-col justify-between overflow-y-auto shadow-2xl z-10 animate-fade-in">
            {navContent}
          </div>
        </div>
      )}
    </>
  );
};
